import { useEffect, type RefObject } from 'react'

const LINE_STEP = 48
const PAGE_FRACTION = 0.9

/** Candidate scroll containers, most specific first. */
const SCROLLER_SELECTORS = ['.content-scroll-area', '.page-overlay-body', '.main-outlet']

function isTextEntry(el: Element | null): boolean {
  if (!el) return false
  const tag = el.tagName
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true
  return (el as HTMLElement).isContentEditable === true
}

/** True when nothing meaningful holds focus, so the page keys belong to the content. */
function focusIsIdle(): boolean {
  const active = document.activeElement
  return !active || active === document.body || active === document.documentElement
}

function isScrollable(el: HTMLElement): boolean {
  if (el.scrollHeight <= el.clientHeight) return false
  const overflowY = getComputedStyle(el).overflowY
  return overflowY === 'auto' || overflowY === 'scroll'
}

/** True when the browser would already scroll something for the focused element. */
function scrollsNatively(active: Element): boolean {
  let el: HTMLElement | null = active as HTMLElement
  while (el) {
    if (isScrollable(el)) return true
    el = el.parentElement
  }
  return false
}

/** True when no focused element has a better claim on the page keys than the content. */
function keysBelongToContent(): boolean {
  const active = document.activeElement
  if (isTextEntry(active)) return false
  if (focusIsIdle() || !active) return true
  // A dialog is open but focus sits outside it — typically restored to the control
  // that opened it, e.g. a menu item. The dialog still owns the page keys.
  const modal = document.querySelector('[aria-modal="true"]')
  if (modal !== null && !modal.contains(active)) return true
  // Focus is parked on a control with nothing scrollable above it (a header button, a
  // menu trigger a closing menu handed focus back to). The keys have no other use.
  return !scrollsNatively(active)
}

/** An open overlay or dialog owns the keys, so only scrollers inside it count. */
function isCoveredByModal(el: Element): boolean {
  const modal = document.querySelector('[aria-modal="true"]')
  return modal !== null && !modal.contains(el)
}

/** The scrollable region the page keys should act on, or null when there is none. */
function findScroller(): HTMLElement | null {
  for (const selector of SCROLLER_SELECTORS) {
    for (const el of document.querySelectorAll<HTMLElement>(selector)) {
      if (isCoveredByModal(el)) continue
      if (el.scrollHeight > el.clientHeight) return el
    }
  }
  return null
}

function scrollFor(el: HTMLElement, e: KeyboardEvent): boolean {
  const page = Math.max(el.clientHeight * PAGE_FRACTION, LINE_STEP)
  switch (e.key) {
    case 'ArrowDown':
      el.scrollBy({ top: LINE_STEP })
      return true
    case 'ArrowUp':
      el.scrollBy({ top: -LINE_STEP })
      return true
    case 'PageDown':
      el.scrollBy({ top: page })
      return true
    case 'PageUp':
      el.scrollBy({ top: -page })
      return true
    case ' ':
      el.scrollBy({ top: e.shiftKey ? -page : page })
      return true
    case 'Home':
      el.scrollTo({ top: 0 })
      return true
    case 'End':
      el.scrollTo({ top: el.scrollHeight })
      return true
    default:
      return false
  }
}

/**
 * Moves focus to a scrollable content region whenever `contentKey` changes, so the
 * browser scrolls it natively on arrow / page / home / end keys after a navigation.
 *
 * The element must carry `tabIndex={0}`, which also makes a click inside it hand
 * focus to the region rather than dropping focus on the body.
 */
export function useContentScrollArea(
  ref: RefObject<HTMLElement | null>,
  contentKey: unknown,
): void {
  useEffect(() => {
    const el = ref.current
    if (!el) return
    // A region inside a dialog claims focus on open; anything else yields to a
    // field the user is typing in and to whatever dialog is already on screen.
    if (el.closest('[aria-modal="true"]') === null) {
      if (isTextEntry(document.activeElement) || isCoveredByModal(el)) return
    }
    el.focus({ preventScroll: true })
  }, [ref, contentKey])
}

/**
 * App-wide fallback: when focus has landed on the body (nothing focusable was clicked)
 * the page keys would otherwise do nothing, because html and body are `overflow: hidden`
 * and every scroll region is a nested element. Routes them to the active scroll region.
 *
 * Install once, in the app shell.
 */
export function useGlobalContentKeyScroll(): void {
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.altKey || e.metaKey || !keysBelongToContent()) return
      const el = findScroller()
      if (el && scrollFor(el, e)) e.preventDefault()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [])
}
